(ns url.core
  "URL utility functions shared across platform and browser-extension.
   Functions that need a public suffix list take `public-suffix?` as their
   first parameter — each project provides its own loading mechanism."
  (:require
   [clojure.string :as str]
   [url.linkedin :as linkedin]))

(defn url->host
  "Extract the host (without scheme, path, or query) from a URL string."
  [url]
  (when url
    (-> url
        (str/replace #"^blob:" "")
        (str/replace #"https?://" "")
        (str/split #"\?|/")
        first)))

(defn url->display-host
  "Like `url->host` but strips a leading `www.` so the result reads
  cleanly in UI labels (e.g. `github.com` instead of `www.github.com`)."
  [url]
  (when-let [host (url->host url)]
    (if (str/starts-with? host "www.")
      (subs host 4)
      host)))

(defn url->display-label
  "Schemeless, `www.`-less form of `url` — the full URL (path included)
  for compact link labels. The href should stay intact for navigation."
  [url]
  (when url
    (-> url
        (str/replace #"^https?://" "")
        (str/replace #"^www\." ""))))

(def extension-params
  "The browser extension's own sidebar query params, injected into a page's URL
  to drive sidebar UI state — never part of the real page address."
  #{"__humble_sidebar_open" "__humble_sidebar_peek_out"})

(def prune-param?
  "Predicate over a query-param name — true when the param should be dropped
  before a URL is persisted as a saved link (params that don't change which page
  the URL addresses). Extend pruning by adding a predicate to this `some-fn`,
  e.g. `#(str/starts-with? % \"utm_\")` for analytics params."
  (some-fn extension-params))

(defn prune-params
  "Returns `url` with every `prune-param?` query param removed, leaving the path,
  the remaining query params, and the fragment intact; returns `url` unchanged
  when it carries none. Pure — backs both on-save pruning and the migration that
  cleans already-saved links."
  [url]
  (if (nil? url)
    url
    (let [[path+query fragment] (str/split url #"#" 2)
          [path query] (str/split path+query #"\?" 2)]
      (if (str/blank? query)
        url
        (let [kept (->> (str/split query #"&")
                        (remove (fn [param]
                                  (prune-param? (first (str/split param #"=" 2)))))
                        (str/join "&"))]
          (str path
               (when (seq kept) (str "?" kept))
               (when fragment (str "#" fragment))))))))

(defn canonical-page
  "Canonical form of `url` for same-page comparison. Drops the bits that don't
  change which page a URL addresses — the scheme, a leading `www.`, a trailing
  `/`, and the fragment — while keeping the path and query string, since query
  params can select a different page."
  [url]
  (when url
    (-> url
        (str/replace #"^https?://" "")
        (str/replace #"^www\." "")
        (str/replace #"#.*$" "")
        (str/replace #"/(?=\?|$)" ""))))

(defn same-page?
  "True when `a` and `b` address the same page, ignoring scheme, a leading
  `www.`, a trailing `/`, and the fragment. Query params are significant."
  [a b]
  (and (some? a) (some? b)
       (= (canonical-page a) (canonical-page b))))

(defn find-true-domain
  "Strip subdomains (except for public suffixes) from the url.
   Return domain (host) only.
   `public-suffix?` is a set/predicate of known public suffixes."
  [public-suffix? url]
  (let [domain-part (url->host url)]
    (cond
      (nil? domain-part)
      nil

      (str/blank? domain-part)
      ""

      :else
      (loop [domain (str/split domain-part #"\.")]
        (if (public-suffix? (str/join "." (next domain)))
          (str/join "." domain)
          (if (< 2 (count domain))
            (recur (next domain))
            (str/join "." domain)))))))

(def aggregation-sites
  "Sites with URL patterns for resource-key extraction (e.g. LinkedIn profiles, X posts)."
  {"linkedin.com" [linkedin/person-url-pattern
                   linkedin/recruiter-person-url-pattern
                   linkedin/company-url-pattern]
   "x.com" [#"(x.com/[^?]*)(\?[^\/]*)?$"]})

(def ai-sites
  "AI/LLM site domains with URL patterns for resource-key extraction (e.g. a
   single Claude chat, a single ChatGPT conversation)."
  {"claude.ai" [#"(claude.ai/chat/[^?]*)(\?[^\/]*)?$"]
   "chatgpt.com" [#"(chatgpt.com/c/[^?]*)(\?[^\/]*)?$"
                  #"(chatgpt.com/g/[^?]*)(\?[^\/]*)?$"]
   "gemini.google.com" [#"(gemini.google.com/app/[^?]*)(\?[^\/]*)?$"]
   "aistudio.google.com" [#"(aistudio.google.com/apps/drive/[^?]*)(\?[^\/]*)?$"]
   "v0.app" [#"(v0.app/chat/[^?]*)(\?[^\/]*)?$"]
   "replit.com" [#"(replit.com/[^?]*)(\?[^\/]*)?$"]
   "lovable.dev" [#"(lovable.dev/projects/[^?]*)(\?[^\/]*)?$"]
   "bolt.new" [#"(bolt.new/~/[^?]*)(\?[^\/]*)?$"]})

(defn- match-patterns
  [url patterns]
  (some (fn [pattern]
          (second (re-find pattern url)))
        patterns))

(defn- find-pattern-match
  "Look up patterns for `host` or `domain` in `sites` and try to match `url`.
   Returns [true match] if patterns exist (match may be nil),
   or [false nil] if no patterns found for this site."
  [sites host domain url]
  (if-let [patterns (or (get sites host)
                        (get sites domain))]
    [true (match-patterns url patterns)]
    [false nil]))

(defn url->resource-key
  "The string to search for in a table like Notion or Airtable, based on the
   current page. `public-suffix?` is a set/predicate of known public suffixes."
  [public-suffix? location]
  (let [url (str location)
        host (url->host location)
        domain (find-true-domain public-suffix? location)
        [aggregation? aggregation-match] (find-pattern-match aggregation-sites host domain url)
        [_ai? ai-match] (find-pattern-match ai-sites host domain url)]
    ;; Aggregation sites return nil on no-match because we don't want to
    ;; look up LinkedIn/X as entities. AI sites fall through to domain so
    ;; we can still look up the entity behind the AI session.
    (cond
      aggregation-match aggregation-match
      aggregation?      nil
      ai-match          ai-match
      :else             domain)))

(defn valid-http-url?
  "True when `s` is a syntactically valid absolute http(s) URL with a host.
   Guards user-supplied links before they're persisted."
  [s]
  (when (string? s)
    (let [s (str/trim s)]
      #?(:clj
         (try
           (let [uri (java.net.URI. s)
                 scheme (some-> (.getScheme uri) str/lower-case)]
             (boolean (and (#{"http" "https"} scheme)
                           (seq (.getHost uri)))))
           (catch Exception _ false))
         :cljs
         (try
           (let [u (js/URL. s)
                 scheme (str/lower-case (.-protocol u))]
             (boolean (and (#{"http:" "https:"} scheme)
                           (seq (.-hostname u)))))
           (catch :default _ false))))))

(defn normalize-http-url
  "Coerces user input into a valid absolute http(s) URL: returns `s` trimmed
   when it already is one, prepends `https://` when only the scheme is missing
   (e.g. `google.com`), and returns nil when no valid URL can be made."
  [s]
  (when (string? s)
    (let [trimmed (str/trim s)]
      (cond
        (str/blank? trimmed) nil
        (valid-http-url? trimmed) trimmed
        (and (not (str/includes? trimmed "://"))
             (valid-http-url? (str "https://" trimmed))) (str "https://" trimmed)
        :else nil))))

(defn url-changed?
  "Determines if navigation has occurred between two URLs based on their
   resource keys. `public-suffix?` is a set/predicate of known public suffixes."
  [public-suffix? from-url to-url]
  (if (and (or (nil? from-url)
               (nil? to-url))
           (not= from-url to-url))
    true
    (let [from-meaningful-part (url->resource-key public-suffix? from-url)
          to-meaningful-part (url->resource-key public-suffix? to-url)]
      (not= from-meaningful-part
            to-meaningful-part))))
